import Button, { IconButton } from '@asphalt-react/button';
import { sendStyles, Provider as Provider$1 } from '@asphalt-react/context';
import { ChevronLeft, CaretUp, CaretDown, ChevronRight, Calendar as Calendar$1 } from '@asphalt-react/iconpack';
import Popover, { toggle } from '@asphalt-react/popover';
import { Datefield } from '@asphalt-react/textfield';
import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { createContext, useRef, useContext, useLayoutEffect, useEffect, useState, useMemo } from 'react';
import { propsUtil, KEYCODES } from '@asphalt-react/helper';
import { isValid as isValid$1, startOfWeek, isSameMonth as isSameMonth$1, addDays as addDays$1, subDays as subDays$1, isSameDay as isSameDay$1, addYears as addYears$1, subYears as subYears$1, addMonths as addMonths$1, subMonths as subMonths$1, setDate as setDate$1, setYear as setYear$1, isBefore as isBefore$1, isAfter as isAfter$1, differenceInCalendarYears, format as format$1, parse as parse$1 } from 'date-fns';
import { enIN, enUS, id, vi, th } from 'date-fns/locale';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".DatePicker__JsOpF {\n  --datepicker-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --datepicker-grid-border: var(\n    --background-border-solid-primary,\n    solid hsla(0, 0%, 86%, 1)\n  );\n  --datepicker-border-width: var(--border-width, 1px);\n  --datepicker-date-background: var(--interactive-fill-alternate, transparent);\n  --datepicker-day-font-size: var(--body-s, 0.88rem);\n  --datepicker-date-font-size: var(--label-s, 0.88rem);\n  --datepicker-day-text-color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  --datepicker-text-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --datepicker-date-border-color: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --datepicker-border-radius: var(--roundness-1, 2px);\n\n  color: var(--datepicker-font-color);\n  display: inline-block;\n  position: relative;\n  /* In a grid-layout, DatePicker's outermost element stretches to reserve all its allotted space.\n    But, it should have the same width as Datefield. So DatePicker relies on Datefield to control the width.\n  */\n  width: -webkit-fit-content;\n  width: -moz-fit-content;\n  width: fit-content;\n}\n\n.stretch__dccN6 {\n  width: 100%;\n}\n\n.calendar__GQ-G1 {\n  width: 320px;\n  background-color: white;\n  display: grid;\n  box-sizing: border-box;\n}\n\n.largeCalendar__gyWmW {\n  width: 376px;\n}\n\n.header__t9zcG {\n  padding: 0.25rem;\n  display: flex;\n  justify-content: space-between;\n}\n\n.tapArea__xWYko {\n  display: inline-flex;\n  width: 64px;\n  height: 40px;\n}\n\n.labelWrap__GVvSC {\n  display: inline-block;\n  align-self: center;\n}\n\n.headerYear__1plRO {\n  justify-content: center;\n  padding: 0.66rem;\n}\n\n.largeHeaderYear__7LuJk {\n  padding: 0.44rem;\n}\n\n.month__iZVZf {\n  font-weight: 600;\n  margin-right: 0.5rem;\n}\n\n.grid__iUs04 {\n  overflow-y: auto;\n  border-top: var(--datepicker-border-width) var(--datepicker-grid-border);\n  padding: 1rem 1.5rem;\n  height: 300px;\n}\n\n.largeGrid__KLW-2 {\n  height: 350px;\n}\n\n.dayGrid__bo4wd {\n  display: grid;\n  grid-template-columns: repeat(7, 1fr);\n  grid-gap: 0.5rem;\n  margin-bottom: 1rem;\n}\n\n.dateGrid__1UY1L {\n  display: grid;\n  grid-template: repeat(6, 1fr) / repeat(7, 1fr);\n  grid-gap: 0.5rem;\n}\n\n.dateTile__YRiMr {\n  display: inline-block;\n  text-align: center;\n  align-self: center;\n  background-color: var(--datepicker-date-background);\n}\n\n.day__os9u1 {\n  font-size: var(--datepicker-day-font-size);\n  color: var(--datepicker-day-text-color);\n}\n\n.largeDay__WX17u {\n  --datepicker-day-font-size: var(--body-m, 1rem);\n}\n\n.weekend__jHoue {\n  --datepicker-day-text-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n}\n\n.date__FrywU {\n  color: var(--datepicker-text-color);\n  font-size: var(--datepicker-date-font-size);\n  font-weight: 600;\n  border: var(--datepicker-border-width) solid transparent;\n  border-radius: var(--datepicker-border-radius);\n  padding: 0.5rem 0;\n}\n\n.largeDate__x9zcs {\n  padding: 0.68rem 0.53rem;\n}\n\n.largeDate__x9zcs,\n.largeYear__UDHgK {\n  --datepicker-date-font-size: var(--label-m, 1rem);\n}\n\n.today__u7eYa {\n  --datepicker-date-background: var(\n    --support-info-fill,\n    hsla(210, 67%, 94%, 1)\n  );\n}\n\n.restricted__g9Blt {\n  --datepicker-text-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --datepicker-date-background: var(--interactive-fill-alternate, transparent);\n  pointer-events: none;\n  opacity: 0.2;\n}\n\n.yearGrid__6QpFU {\n  display: grid;\n  grid-template: repeat(7, 1fr) / repeat(4, 1fr);\n  grid-gap: 1.25rem 0.75rem;\n}\n\n.largeYearGrid__53EKL {\n  grid-gap: 1.5rem;\n}\n\n.year__8FSWf {\n  display: inline-block;\n  padding: 0.38rem 0.74rem;\n  text-align: center;\n  font-weight: 600;\n  border: var(--datepicker-border-width) solid transparent;\n  border-radius: var(--datepicker-border-radius);\n  font-size: var(--datepicker-date-font-size);\n  background-color: var(--datepicker-date-background);\n  color: var(--datepicker-text-color);\n}\n\n.largeYear__UDHgK {\n  padding: 0.44rem 0.75rem;\n}\n\n.selected__-wThF {\n  --datepicker-text-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --datepicker-date-background: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n}\n\n.disabled__YdbQE {\n  --datepicker-text-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --datepicker-date-background: var(--interactive-fill-alternate, transparent);\n  pointer-events: none;\n}\n\n.date__FrywU:not(.selected__-wThF):hover,\n.year__8FSWf:not(.selected__-wThF):hover {\n  --datepicker-date-background: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n}\n\n.date__FrywU:focus,\n.year__8FSWf:focus {\n  outline: none;\n}\n\n.date__FrywU:not(.selected__-wThF):focus,\n.year__8FSWf:not(.selected__-wThF):focus {\n  --datepicker-date-background: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n  border-color: var(--datepicker-date-border-color);\n}\n\n.dummyTarget__hybTS {\n  position: absolute;\n  right: 0;\n  height: 40px;\n}\n\n.largeDummyTarget__Ud2-8 {\n  height: 48px;\n}\n";
var styles = {"DatePicker":"DatePicker__JsOpF","stretch":"stretch__dccN6","calendar":"calendar__GQ-G1","largeCalendar":"largeCalendar__gyWmW","header":"header__t9zcG","tapArea":"tapArea__xWYko","labelWrap":"labelWrap__GVvSC","headerYear":"headerYear__1plRO","largeHeaderYear":"largeHeaderYear__7LuJk","month":"month__iZVZf","grid":"grid__iUs04","largeGrid":"largeGrid__KLW-2","dayGrid":"dayGrid__bo4wd","dateGrid":"dateGrid__1UY1L","dateTile":"dateTile__YRiMr","day":"day__os9u1","largeDay":"largeDay__WX17u","weekend":"weekend__jHoue","date":"date__FrywU","largeDate":"largeDate__x9zcs","largeYear":"largeYear__UDHgK","today":"today__u7eYa","restricted":"restricted__g9Blt","yearGrid":"yearGrid__6QpFU","largeYearGrid":"largeYearGrid__53EKL","year":"year__8FSWf","selected":"selected__-wThF","disabled":"disabled__YdbQE","dummyTarget":"dummyTarget__hybTS","largeDummyTarget":"largeDummyTarget__Ud2-8"};
var stylesheet=".DatePicker__JsOpF {\n  --datepicker-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --datepicker-grid-border: var(\n    --background-border-solid-primary,\n    solid hsla(0, 0%, 86%, 1)\n  );\n  --datepicker-border-width: var(--border-width, 1px);\n  --datepicker-date-background: var(--interactive-fill-alternate, transparent);\n  --datepicker-day-font-size: var(--body-s, 0.88rem);\n  --datepicker-date-font-size: var(--label-s, 0.88rem);\n  --datepicker-day-text-color: var(--text-muted, hsla(219, 23%, 60%, 1));\n  --datepicker-text-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --datepicker-date-border-color: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --datepicker-border-radius: var(--roundness-1, 2px);\n\n  color: var(--datepicker-font-color);\n  display: inline-block;\n  position: relative;\n  /* In a grid-layout, DatePicker's outermost element stretches to reserve all its allotted space.\n    But, it should have the same width as Datefield. So DatePicker relies on Datefield to control the width.\n  */\n  width: -webkit-fit-content;\n  width: -moz-fit-content;\n  width: fit-content;\n}\n\n.stretch__dccN6 {\n  width: 100%;\n}\n\n.calendar__GQ-G1 {\n  width: 320px;\n  background-color: white;\n  display: grid;\n  box-sizing: border-box;\n}\n\n.largeCalendar__gyWmW {\n  width: 376px;\n}\n\n.header__t9zcG {\n  padding: 0.25rem;\n  display: flex;\n  justify-content: space-between;\n}\n\n.tapArea__xWYko {\n  display: inline-flex;\n  width: 64px;\n  height: 40px;\n}\n\n.labelWrap__GVvSC {\n  display: inline-block;\n  align-self: center;\n}\n\n.headerYear__1plRO {\n  justify-content: center;\n  padding: 0.66rem;\n}\n\n.largeHeaderYear__7LuJk {\n  padding: 0.44rem;\n}\n\n.month__iZVZf {\n  font-weight: 600;\n  margin-right: 0.5rem;\n}\n\n.grid__iUs04 {\n  overflow-y: auto;\n  border-top: var(--datepicker-border-width) var(--datepicker-grid-border);\n  padding: 1rem 1.5rem;\n  height: 300px;\n}\n\n.largeGrid__KLW-2 {\n  height: 350px;\n}\n\n.dayGrid__bo4wd {\n  display: grid;\n  grid-template-columns: repeat(7, 1fr);\n  grid-gap: 0.5rem;\n  margin-bottom: 1rem;\n}\n\n.dateGrid__1UY1L {\n  display: grid;\n  grid-template: repeat(6, 1fr) / repeat(7, 1fr);\n  grid-gap: 0.5rem;\n}\n\n.dateTile__YRiMr {\n  display: inline-block;\n  text-align: center;\n  align-self: center;\n  background-color: var(--datepicker-date-background);\n}\n\n.day__os9u1 {\n  font-size: var(--datepicker-day-font-size);\n  color: var(--datepicker-day-text-color);\n}\n\n.largeDay__WX17u {\n  --datepicker-day-font-size: var(--body-m, 1rem);\n}\n\n.weekend__jHoue {\n  --datepicker-day-text-color: var(\n    --support-danger-default,\n    hsla(12, 92%, 45%, 1)\n  );\n}\n\n.date__FrywU {\n  color: var(--datepicker-text-color);\n  font-size: var(--datepicker-date-font-size);\n  font-weight: 600;\n  border: var(--datepicker-border-width) solid transparent;\n  border-radius: var(--datepicker-border-radius);\n  padding: 0.5rem 0;\n}\n\n.largeDate__x9zcs {\n  padding: 0.68rem 0.53rem;\n}\n\n.largeDate__x9zcs,\n.largeYear__UDHgK {\n  --datepicker-date-font-size: var(--label-m, 1rem);\n}\n\n.today__u7eYa {\n  --datepicker-date-background: var(\n    --support-info-fill,\n    hsla(210, 67%, 94%, 1)\n  );\n}\n\n.restricted__g9Blt {\n  --datepicker-text-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --datepicker-date-background: var(--interactive-fill-alternate, transparent);\n  pointer-events: none;\n  opacity: 0.2;\n}\n\n.yearGrid__6QpFU {\n  display: grid;\n  grid-template: repeat(7, 1fr) / repeat(4, 1fr);\n  grid-gap: 1.25rem 0.75rem;\n}\n\n.largeYearGrid__53EKL {\n  grid-gap: 1.5rem;\n}\n\n.year__8FSWf {\n  display: inline-block;\n  padding: 0.38rem 0.74rem;\n  text-align: center;\n  font-weight: 600;\n  border: var(--datepicker-border-width) solid transparent;\n  border-radius: var(--datepicker-border-radius);\n  font-size: var(--datepicker-date-font-size);\n  background-color: var(--datepicker-date-background);\n  color: var(--datepicker-text-color);\n}\n\n.largeYear__UDHgK {\n  padding: 0.44rem 0.75rem;\n}\n\n.selected__-wThF {\n  --datepicker-text-color: var(--text-inverse, hsla(0, 0%, 100%, 1));\n  --datepicker-date-background: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n}\n\n.disabled__YdbQE {\n  --datepicker-text-color: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --datepicker-date-background: var(--interactive-fill-alternate, transparent);\n  pointer-events: none;\n}\n\n.date__FrywU:not(.selected__-wThF):hover,\n.year__8FSWf:not(.selected__-wThF):hover {\n  --datepicker-date-background: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n}\n\n.date__FrywU:focus,\n.year__8FSWf:focus {\n  outline: none;\n}\n\n.date__FrywU:not(.selected__-wThF):focus,\n.year__8FSWf:not(.selected__-wThF):focus {\n  --datepicker-date-background: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n  border-color: var(--datepicker-date-border-color);\n}\n\n.dummyTarget__hybTS {\n  position: absolute;\n  right: 0;\n  height: 40px;\n}\n\n.largeDummyTarget__Ud2-8 {\n  height: 48px;\n}\n";
styleInject(css_248z);

const context$1 = /*#__PURE__*/createContext("tab");
const DateProvider = context$1.Provider;

const context = /*#__PURE__*/createContext(null);
const Provider = context.Provider;

const adapter = {
  isValid: date => isValid$1(date),
  getStartOfWeek: ({
    date,
    options
  }) => startOfWeek(date, options),
  isSameMonth: (date1, date2) => isSameMonth$1(date1, date2),
  addDays: (date, numberOfDays) => addDays$1(date, numberOfDays),
  subDays: (date, numberOfDays) => subDays$1(date, numberOfDays),
  isSameDay: (day1, day2) => isSameDay$1(day1, day2),
  addYears: (date, years) => addYears$1(date, years),
  subYears: (date, years) => subYears$1(date, years),
  addMonths: (date, months) => addMonths$1(date, months),
  subMonths: (date, months) => subMonths$1(date, months),
  setDate: (dateObj, date) => setDate$1(dateObj, date),
  setYear: (date, year) => setYear$1(date, year),
  isBefore: (date, dateToCompare) => isBefore$1(date, dateToCompare),
  isAfter: (date, dateToCompare) => isAfter$1(date, dateToCompare),
  differenceInYears: (date1, date2) => differenceInCalendarYears(date1, date2),
  format: (date, dateFormat, options = {}) => format$1(date, dateFormat, options),
  parse: (dateString, format) => parse$1(dateString, format, new Date()),
  locales: {
    enIN,
    enUS,
    id,
    vi,
    th
  }
};

/**
 * size values
 */
const sizes = {
  m: "m",
  l: "l"
};
/**
 * Format to show month and year in calendar
 */

const MONTH_YEAR_FORMAT = "MMMM yyyy";
/**
 * Format to show and type date in date field
 */

const DATE_FORMAT = "yyyy-MM-dd";
/**
 * Number of date tiles to show in calendar
 */

const TOTAL_DATE_TILES = 42;
/**
 * Default non-working days
 */

const WEEKENDS = [0, 6];
const PRO_VARIANT = "pro";
const DOB_VARIANT = "dob";
const EVENT_VARIANT = "event";
/**
 * Number of years to render before current year for pro variant
 */

const PRO_MIN_YEARS = 10;
/**
 * Number of years to render after current year for pro variant
 */

const PRO_MAX_YEARS = 10;
/**
 * Number of years to render before current year for dob variant
 */

const DOB_MIN_YEARS = 85;
/**
 * Number of years to render after current year for dob variant
 */

const DOB_MAX_YEARS = 13;
/**
 * Number of months to render after current month for event variant
 */

const EVENT_MAX_MONTHS = 6;
/**
 * Error message for invalid date in `value` prop
 */

const INVALID_DATE = `DatePicker: Pass a valid date in "value"`;
/**
 * Error message for invalid date in `minDate` prop
 */

const INVALID_MINDATE = `DatePicker: Pass a valid date in "minDate"`;
/**
 * Error message for invalid date in `maxDate` prop
 */

const INVALID_MAXDATE = `DatePicker: Pass a valid date in "maxDate"`;
/**
 * Error message for invalid day in `weekStartsOn` prop
 */

const INVALID_WEEK_STARTS_ON = `DatePicker: Pass a valid day in "weekStartsOn"`;
/**
 * Error message for invalid day in `nonWorkingDays` prop
 */

const INVALID_NON_WORKING_DAYS = `DatePicker: Pass a valid day in "nonWorkingDays"`;
/**
 * Error message for invalid range
 */

const INVALID_RANGE = `DatePicker: Pass "maxDate" which is ahead of "minDate"`;
/**
 * Error message for invalid date entered by user
 */

const USER_INVALID_DATE = `DatePicker: Type a valid date in ${DATE_FORMAT.toLowerCase()} format`;
/**
 * Shortened weekdays
 */

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
/**
 * Delay in milliseconds to close Calendar on selecting a date
 *
 * Gives user time to see the selected date
 */

const CLOSE_DELAY = 200;

const {
  isSameMonth,
  getStartOfWeek,
  format,
  isValid,
  addDays,
  isSameDay,
  setDate,
  subYears,
  addYears,
  addMonths,
  isBefore,
  isAfter,
  subMonths,
  subDays,
  setYear,
  differenceInYears,
  locales,
  parse
} = adapter;
const {
  createPropOccurValidator
} = propsUtil;
/**
 * Returns locale object for a language
 *
 * @param {Object | String} locale language's locale
 * @returns {Object}
 */

const getLocale = locale => {
  if (typeof locale === "object") return locale;
  const {
    enIN,
    enUS,
    id,
    vi,
    th
  } = locales;
  if (locale === enIN.code) return enIN;
  if (locale === enUS.code) return enUS;
  if (locale === id.code) return id;
  if (locale === vi.code) return vi;
  if (locale === th.code) return th;
  return enIN;
};
/**
 * Checks the validity of a date
 *
 * @param {Object} date
 * @returns {boolean}
 */

const isValidDate = date => {
  if (typeof date !== "object") return false;

  try {
    return isValid(date);
  } catch (e) {
    return false;
  }
};
/**
 * Checks the availability of any argumeent
 *
 * @param {Object} args
 * @returns {boolean}
 */

const hasValue = args => Boolean(args);
/**
 * Checks the existence as well as validity of a date
 *
 * @param {Object} date
 * @returns {boolean}
 */

const hasValidDate = date => hasValue(date) && isValidDate(date);
/**
 * Checks if min date is ahead of max date
 *
 * @param {Object} range range of dates
 * @param {Object} range.minDate minimum date
 * @param {Object} range.maxDate maximum date
 * @returns {boolean}
 */

const isInvalidRange = ({
  min,
  max
}) => isBefore(max, min);
/**
 * Checks for 'pro' variant
 *
 * @param {string} variant
 * @returns {boolean}
 */

const isProVariant = variant => variant === PRO_VARIANT;
/**
 * Checks for 'dob' variant
 *
 * @param {string} variant
 * @returns {boolean}
 */

const isDobVariant = variant => variant === DOB_VARIANT;
/**
 * Checks for 'event' variant
 *
 * @param {string} variant
 * @returns {boolean}
 */

const isEventVariant = variant => variant === EVENT_VARIANT;
/**
 * converts format to lowercase for display purposes
 * @param {string} format date format
 */

const displayFormat = (format = DATE_FORMAT) => format.toLowerCase();
/**
 * Returns label for calendar
 *
 * @param {Object} date
 * @returns {string}
 */

const getLabel = (date, locale) => format(date, MONTH_YEAR_FORMAT, {
  locale
});
/**
 * Rotates an array by a certain index
 *
 * @param {Array} arr array to update
 * @param {number} index position to shift first element to
 * @returns {Array}
 */

const rotateByIndex = (arr, index) => [...arr.slice(index), ...arr.slice(0, index)];
/**
 * Generates an array of number of desired length
 *
 * @param {number} length length of array
 * @returns {Array}
 */

const generateArrayOfN = length => [...Array(length).keys()];
/**
 * Adds a number to each item of an array
 *
 * @param {number} addend number to add
 * @return {Array}
 */

const addToListItems = addend => item => item + addend;
/**
 * Checks if a date falls before the min date
 *
 * @param {Object} date
 * @param {Object} minDate
 * @returns {boolean}
 */

const beforeMindate = (date, minDate) => isBefore(date, minDate);
/**
 * Checks if a date falls after the max date
 *
 * @param {Object} date
 * @param {Object} maxDate
 * @returns {boolean}
 */

const afterMaxdate = (date, maxDate) => isAfter(date, maxDate);
/**
 * Returns the same date of the previous month
 *
 * @param {Object} date
 * @returns {Object}
 */

const getPreviousMonth = date => subMonths(date, 1);
/**
 * Returns the same date of the next month
 *
 * @param {Object} date
 * @returns {Object}
 */

const getNextMonth = date => addMonths(date, 1);
/**
 * Returns the same date of the previous year
 *
 * @param {Object} date
 * @returns {Object}
 */

const getPreviousYear = date => subYears(date, 1);
/**
 * Returns the same date of the next year
 *
 * @param {Object} date
 * @returns {Object}
 */

const getNextYear = date => addYears(date, 1);
/**
 * Returns the previous date for a given date
 *
 * @param {Object} date
 * @returns {Object}
 */

const getPreviousDay = date => subDays(date, 1);
/**
 * Returns the next date for a given date
 *
 * @param {Object} date
 * @returns {Object}
 */

const getNextDay = date => addDays(date, 1);
/**
 * Returns the same date of the previous week
 *
 * @param {Object} date
 * @returns {Object}
 */

const getPreviousWeek = date => subDays(date, 7);
/**
 * Returns the same date of the next week
 *
 * @param {Object} date
 * @returns {Object}
 */

const getNextWeek = date => addDays(date, 7);
/**
 * Returns the same date of a given year
 *
 * @param {Object} date
 * @param {number} year year to set
 * @returns {Object}
 */

const getDateWithYear = (date, year) => setYear(date, year);
/**
 * Checks if a given date falls in the same month of the minimum or maximum date
 *
 * @param {Object} borderDate minimum or maximum selectable date
 * @param {Object} date
 * @returns {boolean}
 */

const isBorderMonth = (borderDate, date) => isSameMonth(borderDate, date);
/**
 * Checks if a date falls withing the range specified by minimum and maximum date
 *
 * @param {Object} param
 * @param {Object} param.date date to check
 * @param {Object} param.minDate minimum date
 * @param {Objectt} param.maxDate maximum date
 * @returns {boolean}
 */

const isWithinRange = ({
  date,
  minDate,
  maxDate
}) => !afterMaxdate(date, maxDate) && !beforeMindate(date, minDate);
/**
 * Checks if a date falls in the adjacent month of the date of displayed month
 *
 * @param {Object} date date of adjacent month
 * @param {*} dayOfDisplayedMonth
 * @returns {boolean}
 */

const shouldShowDisabled = (date, dayOfDisplayedMonth) => !isSameMonth(date, dayOfDisplayedMonth);
/**
 * Checks if the date is selected and is also of the displayed month
 *
 * @param {Object} date
 * @param {Object} selectedDate
 * @param {*} dayOfDisplayedMonth
 */

const shouldShowSelected = (date, selectedDate, dayOfDisplayedMonth) => isValidDate(selectedDate) ? isSameDay(date, selectedDate) && isSameMonth(date, dayOfDisplayedMonth) : false;
/**
 * Checks if a date is today's date and is also of the month displayed
 *
 * @param {Object} date
 * @param {Object} dayOfDisplayedMonth
 * @returns {boolean}
 */

const shouldShowToday = (date, dayOfDisplayedMonth) => isSameDay(date, new Date()) && isSameMonth(date, dayOfDisplayedMonth);
/**
 * Checks if a weekday is a non-working day
 *
 * @param {Object} param
 * @param {Object} param.day weekday
 * @param {number[]} param.nonWorkingDays
 * @param {string} param.variant
 * @returns {boolean}
 */

const shouldShowWeekend = ({
  day,
  nonWorkingDays,
  variant
}) => {
  if (isDobVariant(variant)) return false;
  return nonWorkingDays.indexOf(day) > -1;
};
/**
 * Checks if a date should receive focus
 *
 * @param {Object} date
 * @param {Object} dayOfDisplayedMonth
 * @returns {boolean}
 */

const shouldAddFocus = (date, dayOfDisplayedMonth) => isSameDay(date, dayOfDisplayedMonth);
/**
 * Generates dates of the month to display
 *
 * @param {Object} param
 * @param {Object} param.locale locale of a language
 * @param {number} param.weekStartsOn first day of the week
 * @param {Object} param.date a date of the month to display
 * @returns {Object[]}
 */

const getCalendricalDates = ({
  locale,
  weekStartsOn,
  date
}) => {
  const firstDayOfMonth = setDate(date, 1);
  const firstWeekday = getStartOfWeek({
    date: firstDayOfMonth,
    options: {
      locale,
      weekStartsOn
    }
  });
  return generateArrayOfN(TOTAL_DATE_TILES).reduce((acc, num) => [...acc, addDays(firstWeekday, num)], []);
};
/**
 * Formats a date
 *
 * @param {Object} date
 * @returns {string}
 */

const formatDate = date => format(date, DATE_FORMAT);
/**
 * Returns a date object from a date string
 *
 * @param {string} date
 * @returns {Object}
 */

const parseDate = date => {
  if (date) {
    return parse(date, DATE_FORMAT);
  }
};
/**
 * Returns numerical value of a weekday
 *
 * @param {string} shortName first 3 leters of a weekday
 * @returns {number}
 */

const getWeekday = shortName => WEEKDAYS.indexOf(shortName.toLowerCase());
/**
 * Sets time of a date to 00:00:00
 *
 * @param {Object} date
 * @returns {Object}
 */

const getDateWithoutTime = (date = new Date()) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
/**
 * Returns the range of selectable dates for 'pro' variant
 *
 * @param {Object} param
 * @param {Object} param.minDate minimum date
 * @param {Object} param.maxDate maximum date
 * @param {Object} param.pivot pivotal date to set range
 * @returns {Object}
 */

const getProRange = ({
  minDate,
  maxDate,
  pivot
}) => {
  const pivotYear = pivot.getFullYear();
  const min = minDate || new Date(pivotYear - PRO_MIN_YEARS, 0);
  const max = maxDate || new Date(pivotYear + PRO_MAX_YEARS, 11, 31);
  return {
    min,
    max
  };
};
/**
 * Returns the range of selectable dates for 'dob' variant
 *
 * @param {Object} param
 * @param {Object} param.minDate minimum date
 * @param {Object} param.maxDate maximum date
 * @returns {Object}
 */


const getDobRange = ({
  minDate,
  maxDate
}) => {
  const todaysDate = getDateWithoutTime(new Date());
  const currentYear = todaysDate.getFullYear();
  const min = minDate || new Date(currentYear - DOB_MIN_YEARS, 0);
  const max = maxDate || new Date(currentYear - DOB_MAX_YEARS, 11, 31);
  return {
    min,
    max
  };
};
/**
 * Returns the range of selectable dates for 'event' variant
 *
 * @param {Object} param
 * @param {Object} param.minDate minimum date
 * @param {Object} param.maxDate maximum date
 * @param {Object} param.pivot pivotal date to set range
 * @returns {Object}
 */


const getEventRange = ({
  minDate,
  maxDate,
  pivot
}) => {
  const dateWithoutTime = getDateWithoutTime(pivot);
  const min = minDate || dateWithoutTime;
  const max = maxDate || addMonths(dateWithoutTime, EVENT_MAX_MONTHS);
  return {
    min,
    max
  };
};
/**
 * Returns the range of selectable dates of a given variant
 *
 * @param {Object} param
 * @param {string} param.variant
 * @param {Object} param.pivot pivotal date to set range
 * @param {Object} param.minDate minimum date
 * @param {Object} param.maxDate maximum date
 * @returns {Object}
 */


const getRange = ({
  variant,
  pivot,
  minDate,
  maxDate
}) => {
  if (isProVariant(variant)) {
    return getProRange({
      minDate,
      maxDate,
      pivot
    });
  }

  if (isDobVariant(variant)) {
    return getDobRange({
      minDate,
      maxDate
    });
  }

  if (isEventVariant(variant)) {
    return getEventRange({
      minDate,
      maxDate,
      pivot
    });
  }
};
/**
 * Generates years to render, starting from the year of min date to the year of max date
 *
 * @param {Object} minDate
 * @param {Object} maxDate
 * @returns {number[]}
 */

const getYears = (minDate, maxDate) => {
  const numberOfYears = Math.abs(differenceInYears(maxDate, minDate)) + 1;
  return generateArrayOfN(numberOfYears).map(addToListItems(minDate.getFullYear()));
};
/**
 * Deduces a variant from the provided variants
 *
 * @param {Object} param
 * @param {boolean} param.pro
 * @param {boolean} param.dob
 * @param {boolean} param.event
 * @returns {string}
 */

const getVariant = ({
  pro,
  dob,
  event
}) => {
  const isSingleton = createPropOccurValidator([PRO_VARIANT, DOB_VARIANT, EVENT_VARIANT])({
    pro,
    dob,
    event
  });
  if (!isSingleton) return PRO_VARIANT;
  if (dob) return DOB_VARIANT;
  if (event) return EVENT_VARIANT;
  return PRO_VARIANT;
};
/**
 * Checks if the pressed key is an arrow key
 *
 * @param {number} keyCode event's keyCode
 * @returns {boolean}
 */

const isArrowKey = keyCode => keyCode === KEYCODES.UP || keyCode === KEYCODES.DOWN || keyCode === KEYCODES.LEFT || keyCode === KEYCODES.RIGHT;
/**
 * Checks the validity of a given size
 *
 * @param {Object} sizes available sizes
 * @param {string} size
 * @returns {boolean}
 */

const isValidSize = (sizes, size) => propsUtil.validateSize(Object.values(sizes), size);
/**
 * Deduces the size
 *
 * @param {Object} sizes available sizes
 * @param {string} size
 * @returns {string}
 */

const getSize = (sizes, size) => isValidSize(sizes, size) ? size : sizes.m;
/**
 * Checks if the size is large
 *
 * @param {string} size
 * @returns {boolean}
 */

const isLargeSized = size => size === sizes.l;
/**
 * Checks if the value in date field is unchanged
 *
 * @param {string} dateString date field value
 * @param {Object} value selected date
 * @returns {boolean}
 */

const isInputUnchanged = (dateString, value) => {
  if (hasValidDate(value)) {
    return dateString === formatDate(value);
  }

  return dateString === "";
};
/**
 * Determines the first day of the week
 *
 * @param {string} day weekday
 * @param {Object} locale locale
 *
 * @throws {InvalidDate}
 *
 * @returns {number}
 */

const getWeekStartsOn = (day, locale) => {
  const {
    weekStartsOn
  } = locale.options;

  if (!day) {
    return weekStartsOn;
  }

  const dayCode = getWeekday(day);
  return dayCode < 0 ? weekStartsOn : dayCode;
};
/**
 * Determines the non-working days in a week
 *
 * @param {string[]} days list of weekdays
 *
 * @throws {InvalidDate}
 *
 * @returns {number[]}
 */

const getNonWorkingDays = days => {
  const dayCodes = days.map(getWeekday);
  return dayCodes.some(code => code < 0) ? WEEKENDS : dayCodes;
};
/**
 * Returns error message for RangeBreach error
 *
 * @param {Object} range range of selectable date
 * @returns {string}
 */

const getRangeBreachMsg = range => `DatePicker: Pass a date which lies between ${formatDate(range.min)} and ${formatDate(range.max)}`;
/**
 * Returns error message for UserRangeBreach error
 *
 * @param {Object} range range of selectable date
 * @returns {string}
 */

const getUserRangeBreachMsg = range => `DatePicker: Type a date which lies between ${formatDate(range.min)} and ${formatDate(range.max)}`;
/**
 * Checks if a browser supports <input type="date" />
 *
 * @returns {boolean}
 */

const checkTypeDateSupport = () => {
  const dateElement = document.createElement("input");

  try {
    dateElement.type = "date";
  } catch (e) {
    return false;
  }

  return dateElement.type === "date";
};
/**
 * Generates weekdays
 *
 * @param {Object} param0
 * @param {Object} param0.locale language locale
 * @param {number[]} param0.nonWorkingDays non working days in a week
 * @param {string} param0.variant variant of DatePicker
 * @param {number} param0.weekStartsOn first day of a week
 * @returns {Object[]}
 */

const getWeekdays = ({
  locale,
  nonWorkingDays,
  variant,
  weekStartsOn
}) => {
  const days = generateArrayOfN(7).map(day => ({
    value: locale.localize.day(day, {
      width: "short"
    }).toUpperCase(),
    label: locale.localize.day(day),
    weekend: shouldShowWeekend({
      day,
      nonWorkingDays,
      variant
    })
  }));
  const rotationIndex = weekStartsOn === 0 ? 7 : weekStartsOn;
  return rotateByIndex(days, rotationIndex);
};
/**
 * Yields minimum and maximum dates to generate range of selectable dates
 *
 * @param {Object} minDate minimum selectable date
 * @param {Object} maxDate maximum selectable date
 * @returns {Object}
 */

const getMinMax = (minDate, maxDate) => {
  let min = null;
  let max = null;

  if (hasValidDate(minDate)) {
    min = minDate;
  }

  if (hasValidDate(maxDate)) {
    max = maxDate;
  }

  if (min && max && isInvalidRange({
    min,
    max
  })) {
    min = null;
    max = null;
  }

  return {
    minDate: min,
    maxDate: max
  };
};

const DateTile = ({
  date
}) => {
  const ref = useRef(null);
  const {
    size,
    disabled: disableAll
  } = useContext(context$1);
  const {
    onDateSelection,
    shouldFocus,
    setShouldFocus
  } = useContext(context);
  const {
    value,
    selected,
    adjacent,
    restricted,
    today,
    focusable
  } = date;
  const isInteractive = !(restricted || adjacent);
  useLayoutEffect(() => {
    if (focusable && ref && ref.current && shouldFocus) {
      var _ref$current;

      /**
       * React Popper adds content to the top of the page initially,
       * then transitions it to the required position -- the target element
       * So, if we add focus to an element inside Popover, the page scrolls to the top in order to show the focused element
       * To avoid this, we are adding focus to elements after composition (after transform is applied on Popover)
       */
      ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.focus();
      setShouldFocus(false);
    }
  }, [focusable]);
  return /*#__PURE__*/React.createElement("button", {
    className: cn(styles.dateTile, styles.date, {
      [styles.selected]: selected
    }, {
      [styles.disabled]: adjacent
    }, {
      [styles.restricted]: restricted
    }, {
      [styles.today]: today
    }, {
      [styles.largeDate]: isLargeSized(size)
    }),
    type: "button",
    onClick: event => isInteractive ? onDateSelection({
      event,
      date: value
    }) : null,
    "data-testid": "date-tile",
    "aria-selected": selected,
    ref: ref,
    role: "gridcell",
    disabled: adjacent || disableAll,
    tabIndex: focusable ? 0 : -1
  }, value.getDate());
};
DateTile.propTypes = {
  /**
   * Date to display
   */
  date: PropTypes.object
};

const DateGrid = ({
  days,
  weekdays,
  onNavigation
}) => {
  const {
    size
  } = useContext(context$1);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.dayGrid,
    role: "grid"
  }, weekdays.map(({
    value,
    weekend,
    label
  }, index) => /*#__PURE__*/React.createElement("span", {
    key: `day_${index}`,
    className: cn(styles.dateTile, styles.day, {
      [styles.weekend]: weekend
    }, {
      [styles.largeDay]: isLargeSized(size)
    }),
    "data-testid": "day-tile",
    "aria-label": label
  }, value))), /*#__PURE__*/React.createElement("div", {
    className: styles.dateGrid,
    onKeyDown: onNavigation
  }, days.map(day => {
    const {
      value
    } = day;
    return /*#__PURE__*/React.createElement(DateTile, {
      date: day,
      key: value.toString()
    });
  })));
};
DateGrid.propTypes = {
  /**
   * Days to display in calendar
   */
  days: PropTypes.arrayOf(PropTypes.object),

  /**
   * Weekdays to display in calendar
   */
  weekdays: PropTypes.arrayOf(PropTypes.object),

  /**
   * Function to handle arrow key bindings
   */
  onNavigation: PropTypes.func
};

const Year = ({
  year: {
    value,
    selected
  },
  onYearClick
}) => {
  const ref = useRef(null);
  const {
    size,
    disabled
  } = useContext(context$1);
  useEffect(() => {
    if (selected && ref.current.scrollIntoView) {
      ref.current.scrollIntoView({
        block: "nearest"
      });
    }
  }, []);
  return /*#__PURE__*/React.createElement("button", {
    className: cn(styles.year, {
      [styles.selected]: selected
    }, {
      [styles.largeYear]: isLargeSized(size)
    }),
    role: "button",
    type: "button",
    tabIndex: "0",
    "data-testid": "year-tile",
    disabled: disabled,
    onClick: () => onYearClick(value),
    ref: ref
  }, value);
};
Year.propTypes = {
  /**
   * Year to render
   */
  year: PropTypes.object,

  /**
   * Handle click event on year tiles
   */
  onYearClick: PropTypes.func
};

const Header = ({
  yearGridOpen,
  disableButtons,
  onMonthChange,
  label,
  calendarId,
  nextMonthLabel,
  previousMonthLabel,
  toggleYearPanel
}) => {
  const {
    size,
    disabled
  } = useContext(context$1);
  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.header, {
      [styles.headerYear]: yearGridOpen
    }, {
      [styles.largeHeaderYear]: isLargeSized(size) && yearGridOpen
    })
  }, !yearGridOpen && /*#__PURE__*/React.createElement("span", {
    className: styles.tapArea
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    nude: true,
    stretch: true,
    disabled: disableButtons(true) || disabled,
    onClick: () => onMonthChange(true),
    "data-testid": "previous-month",
    "aria-label": previousMonthLabel || "previous month",
    size: size
  }, /*#__PURE__*/React.createElement(ChevronLeft, null))), /*#__PURE__*/React.createElement("span", {
    className: styles.labelWrap
  }, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "year-button",
    "aria-label": label,
    "aria-live": "polite",
    id: calendarId,
    nude: true,
    qualifier: yearGridOpen ? /*#__PURE__*/React.createElement(CaretUp, null) : /*#__PURE__*/React.createElement(CaretDown, null),
    qualifierEnd: true,
    onClick: toggleYearPanel,
    type: "button",
    size: size,
    disabled: disabled
  }, yearGridOpen ? "Select Year" : label, " ")), !yearGridOpen && /*#__PURE__*/React.createElement("span", {
    className: styles.tapArea
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    nude: true,
    stretch: true,
    disabled: disableButtons(false) || disabled,
    onClick: () => onMonthChange(false),
    "data-testid": "next-month",
    "aria-label": nextMonthLabel || "next month",
    size: size
  }, /*#__PURE__*/React.createElement(ChevronRight, null))));
};
Header.propTypess = {
  /**
   * Flag to toggle year grid
   */
  yearGridOpen: PropTypes.bool,

  /**
   * Toggles year grid
   */
  setYearGridOpen: PropTypes.func,

  /**
   * Enables/disables month navigation buttons
   */
  disableButtons: PropTypes.func,

  /**
   * Handles click event on month navigation buttons
   */
  onMonthChange: PropTypes.func,

  /**
   * Label to show month and year
   */
  label: PropTypes.string,

  /**
   * ID to uniquely identify the calendar.
   *
   * _Improves accessibility_
   */
  calendarId: PropTypes.string,

  /**
   * Override `aria-label` for the button to navigate to next month
   *
   * _Improves accessibility_
   */
  nextMonthLabel: PropTypes.string,

  /**
   * Override `aria-label` for the button to navigate to previous month
   *
   * _Improves accessibility_
   */
  previousMonthLabel: PropTypes.string
};

const Calendar = ({
  onDateTransition,
  transitionalDate,
  onDateSelection,
  locale,
  minDate,
  maxDate,
  weekStartsOn,
  value,
  nonWorkingDays,
  variant,
  calendarId,
  nextMonthLabel,
  previousMonthLabel
}) => {
  // flag used to toggle year grid
  const [yearGridOpen, setYearGridOpen] = useState(false); // flag used to stop autofocus on date if changing month via buttons

  const [shouldFocus, setShouldFocus] = useState(true);
  const {
    size
  } = useContext(context$1);
  const [activeElement, setActiveElement] = useState(null);
  const weekdays = getWeekdays({
    locale,
    weekStartsOn,
    variant,
    nonWorkingDays
  }); // days to render for a month and year

  const daysInMonth = getCalendricalDates({
    date: transitionalDate,
    locale,
    weekStartsOn
  }).map(date => ({
    value: date,
    restricted: !isWithinRange({
      date,
      minDate,
      maxDate
    }),
    adjacent: shouldShowDisabled(date, transitionalDate),
    selected: shouldShowSelected(date, value, transitionalDate),
    today: shouldShowToday(date, transitionalDate),
    focusable: shouldAddFocus(date, transitionalDate)
  }));
  const years = getYears(minDate, maxDate).map(year => ({
    value: year,
    selected: year === transitionalDate.getFullYear()
  }));
  /**
   * Handles arrow keypress events to navigate to desired date
   *
   * @param {Object} event React's synthetic event
   * @returns {void}
   */

  const onNavigation = event => {
    const {
      keyCode,
      metaKey,
      altKey
    } = event;
    if (!isArrowKey(keyCode)) return;
    event.preventDefault();
    setShouldFocus(true); // handle left arrow keypress

    if (keyCode === KEYCODES.LEFT) {
      // navigates to same date of previous month
      if (altKey) {
        onDateTransition(getPreviousMonth(transitionalDate));
        return;
      } // navigates to same date of previous year


      if (metaKey) {
        onDateTransition(getPreviousYear(transitionalDate));
        return;
      } // navigates to previous date


      onDateTransition(getPreviousDay(transitionalDate));
    } // handle right arrow keypress


    if (keyCode === KEYCODES.RIGHT) {
      // navigates to same date of next month
      if (altKey) {
        onDateTransition(getNextMonth(transitionalDate));
        return;
      } // navigates to same date of next year


      if (metaKey) {
        onDateTransition(getNextYear(transitionalDate));
        return;
      } // navigates to next date


      onDateTransition(getNextDay(transitionalDate));
    } // handle up arrow keypress


    if (keyCode === KEYCODES.UP) {
      // navigates to same day of the previous week
      onDateTransition(getPreviousWeek(transitionalDate));
    } // handle down arrow keypress


    if (keyCode === KEYCODES.DOWN) {
      // navigates to same day of the next week
      onDateTransition(getNextWeek(transitionalDate));
    }
  };
  /**
   * Navigates to either of the ajacent months when month navigation buttons are clicked
   *
   * @param {boolean} previous pervious month button
   * @returns {void}
   */


  const setAdjacentMonth = (previous = true) => previous ? onDateTransition(getPreviousMonth(transitionalDate)) : onDateTransition(getNextMonth(transitionalDate));
  /**
   * Disables month navigation buttons
   *
   * @param {boolean} previous previous month button
   * @returns {void}
   */


  const disableMonthButtons = (previous = true) => previous ? isBorderMonth(minDate, transitionalDate) : isBorderMonth(maxDate, transitionalDate);
  /**
   * Handles click event on year tile and closes year grid
   *
   * @param {number} year selected year
   * @returns {void}
   */


  const onYearClick = year => {
    onDateTransition(getDateWithYear(transitionalDate, year));
    setYearGridOpen(false);
    activeElement.focus();
  };

  const toggleYearPanel = () => {
    if (!yearGridOpen) {
      setActiveElement(document.activeElement);
      setYearGridOpen(true);
    } else {
      setYearGridOpen(false);
      activeElement.focus();
    }
  };
  /**
   * Closes year panel if open
   *
   * @param {Object} event React synthetic event
   */


  const onEsc = event => {
    if (event.keyCode !== KEYCODES.ESC || !yearGridOpen) return;
    /**
     * Stop event to propagate to `@asphalt-react/popover` and thus prevent closing the popover
     */

    event.stopPropagation();
    /**
     * Remove the below line when we support React v17 as the minimum version
     *
     * In React v < 17, the stopPropagation() fails to stop events attached
     * on the document. Therefore, we added `stopImmediatePropagation()` to force stop propagation
     */

    event.nativeEvent.stopImmediatePropagation();
    toggleYearPanel();
  };

  return /*#__PURE__*/React.createElement(Provider, {
    value: {
      onDateSelection,
      shouldFocus,
      setShouldFocus
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.calendar, {
      [styles.largeCalendar]: isLargeSized(size)
    }),
    "data-testid": "calendar",
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": calendarId,
    onKeyDown: onEsc
  }, /*#__PURE__*/React.createElement(Header, {
    yearGridOpen: yearGridOpen,
    toggleYearPanel: toggleYearPanel,
    onMonthChange: setAdjacentMonth,
    label: getLabel(transitionalDate, locale),
    disableButtons: disableMonthButtons,
    calendarId: calendarId,
    nextMonthLabel: nextMonthLabel,
    previousMonthLabel: previousMonthLabel
  }), /*#__PURE__*/React.createElement("div", {
    className: cn(styles.grid, {
      [styles.largeGrid]: isLargeSized(size)
    })
  }, yearGridOpen ? /*#__PURE__*/React.createElement("div", {
    className: cn(styles.yearGrid, {
      [styles.largeYearGrid]: isLargeSized(size)
    }),
    "data-testid": "year-grid"
  }, years.map(year => /*#__PURE__*/React.createElement(Year, {
    key: `year_${year.value}`,
    year: year,
    onYearClick: onYearClick
  }))) : /*#__PURE__*/React.createElement(DateGrid, {
    weekdays: weekdays,
    days: daysInMonth,
    onNavigation: onNavigation
  }))));
};
Calendar.propTypes = {
  /**
   * Handles date changes
   */
  onDateTransition: PropTypes.func,

  /**
   * Date used for transitions in calendar
   */
  transitionalDate: PropTypes.object,

  /**
   * Handles date selection
   */
  onDateSelection: PropTypes.func,

  /**
   * The language locale to format days and months. It also determines the first day of the week.
   *
   * Checkout [date-fns](https://github.com/date-fns/date-fns/blob/master/src/locale/index.js) for the available locales.
   */
  locale: PropTypes.object,

  /**
   * Minimum allowed date (inclusive)
   */
  minDate: PropTypes.object,

  /**
   * Maximum allowed date (inclusive)
   */
  maxDate: PropTypes.object,

  /**
   * Index of the day on which week starts.
   * Refer [Date.getDay()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getDay) for the values
   */
  weekStartsOn: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),

  /**
   * Selected date. Pass `null` or `undefined` to set today's date
   */
  value: PropTypes.object,

  /**
   * Index of days that are non working days or weekends.
   * Refer [Date.getDay()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getDay) for the values
   */
  nonWorkingDays: PropTypes.arrayOf(PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])),

  /**
   * Variant type. Availabe values are `"pro"`, `"dob"` and `"event"`
   */
  variant: PropTypes.oneOf(["pro", "dob", "event"]),

  /**
   * ID to uniquely identify the calendar.
   *
   * _Improves accessibility_
   */
  calendarId: PropTypes.string,

  /**
   * Override `aria-label` for the button to navigate to next month
   *
   * _Improves accessibility_
   */
  nextMonthLabel: PropTypes.string,

  /**
   * Override `aria-label` for the button to navigate to previous month
   *
   * _Improves accessibility_
   */
  previousMonthLabel: PropTypes.string
};

/**
 * Handles date related errors
 */
class DateError extends Error {}
/**
 * Date is not a valid date object
 */


class InvalidDate extends DateError {
  constructor(params) {
    super(params);
    this.name = "InvalidDate";
  }

}
/**
 * End-user enters an invalid date string or does not enter date in the required format
 */

class UserInvalidDate extends DateError {
  constructor(params) {
    super(params);
    this.name = "UserInvalidDate";
  }

}
/**
 * Minimum selectable date is ahead of maximum selectable date
 */

class InvalidRange extends DateError {
  constructor(params) {
    super(params);
    this.name = "InvalidRange";
  }

}
/**
 * Selected date is not within range of selectable dates
 */

class RangeBreach extends DateError {
  constructor(params) {
    super(params);
    this.name = "RangeBreach";
  }

}
/**
 * Date entered by users is not within range of selectable dates
 */

class UserRangeBreach extends DateError {
  constructor(params) {
    super(params);
    this.name = "UserRangeBreach";
  }

}
/**
 * TODO: masking in Textfield
 */
// export class IncorrectFormat extends DateError {
//   constructor(params) {
//     super(params)
//     this.name = "IncorrectFormat"
//   }
// }

const DatePicker = ({
  value,
  onDateChange,
  locale: localeCode,
  minDate,
  maxDate,
  weekStartsOn: weekStartsOnCode,
  nonWorkingDays: nonWorkingDaysCode,
  pro,
  dob,
  event,
  invalid,
  onError,
  placeholder,
  stretch,
  disabled,
  size,
  calendarId,
  calendarLabel,
  nextMonthLabel,
  previousMonthLabel,
  ...props
}) => {
  //Send styles to server
  sendStyles(stylesheet); // show warning if onDateChange, value & onError prop missing

  let warning = ""; // empty warning message by default

  /**
   * ESLint's [no-undefined][1] rule suggests using `typeof` to check if a value is undefined.
   * [1]: https://eslint.org/docs/rules/no-undefined
   */

  if (typeof value === "undefined") {
    warning = !onDateChange ? `DatePicker: "value" & "onDateChange" props are missing. If the field is changeable use "value" & "onDateChange" prop.` : `DatePicker: You provided an "onDateChange" prop without valid "value" prop. If the field is changeable use "onDateChange" & "value" prop with either null or a Date instance.`;
  } else if ((value || value === null) && !onDateChange) {
    warning = `DatePicker: You provided a "value" prop without an "onDateChange" prop. If the field is changeable use "onDateChange" & "value" prop.`;
  }

  if (warning) {
    console.warn(warning);
  }

  if (!onError) {
    console.warn(`DatePicker: "onError" prop is missing. Use an "onError" prop to handle errors.`);
  } // filtering out style and className

  /* eslint-disable-next-line react/prop-types */


  const {
    style,
    className,
    ...rest
  } = props;
  const ref = useRef(null);
  const iconRef = useRef(null);
  const today = getDateWithoutTime(); // date used for transitions in calendar

  const [transitionalDate, setTransitionalDate] = useState(today); // value for date field

  const [fieldValue, setFieldValue] = useState(""); // render native datepicker

  const [showNative, setShowNative] = useState(false);
  useEffect(() => {
    const isDateSupported = checkTypeDateSupport();

    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      setShowNative(isDateSupported);
      return;
    }

    setShowNative(false);
  }, []);
  /**
   * Calls `onError` function if present
   *
   * @param {Object} args arguments passed in callback
   */

  const callOnError = args => {
    if (onError) {
      console.error(args);
      onError(args);
    }
  };
  /**
   * Calls `onDateChange` function if present
   *
   * @param {object} args arguments passed in callback
   */


  const callOnDateChange = args => {
    if (onDateChange) {
      onDateChange(args);
    }
  };

  const variant = getVariant({
    pro,
    dob,
    event
  });
  const range = useMemo(() => getRange({
    variant,
    ...getMinMax(minDate, maxDate),
    pivot: isValidDate(value) ? value : today
  }), [variant, minDate, maxDate]);
  /**
   * Determines which date to highlight
   *
   * @param {object} date date to highlight
   * @returns {void}
   */

  const onDateTransition = date => {
    if (beforeMindate(date, range.min)) {
      setTransitionalDate(range.min);
    } else if (afterMaxdate(date, range.max)) {
      setTransitionalDate(range.max);
    } else {
      setTransitionalDate(date);
    }
  }; // initializes the calendar


  useEffect(() => {
    const initialDate = isValidDate(value) ? value : today;
    onDateTransition(initialDate);
  }, [value, minDate, maxDate]); // sets value in date field

  useEffect(() => {
    // clear datefield if value is `null` or `undefined`
    value !== null && value !== void 0 ? value : setFieldValue("");

    if (isValidDate(value)) {
      setFieldValue(formatDate(value));
    }
  }, [value]);
  useEffect(() => {
    if (hasValue(value) && !isValidDate(value)) {
      const error = new InvalidDate(INVALID_DATE);
      callOnError(error);
    }
  }, [value]);
  useEffect(() => {
    if (hasValue(minDate) && !isValidDate(minDate)) {
      const error = new InvalidDate(INVALID_MINDATE);
      callOnError(error);
    }
  }, [minDate]);
  useEffect(() => {
    if (hasValue(maxDate) && !isValidDate(maxDate)) {
      const error = new InvalidDate(INVALID_MAXDATE);
      callOnError(error);
    }
  }, [maxDate]);
  useEffect(() => {
    if (hasValidDate(minDate) && hasValidDate(maxDate) && isInvalidRange({
      min: minDate,
      max: maxDate
    })) {
      const error = new InvalidRange(INVALID_RANGE);
      callOnError(error);
    }
  }, [minDate, maxDate]);
  useEffect(() => {
    if (hasValidDate(value) && !isWithinRange({
      date: value,
      minDate: range.min,
      maxDate: range.max
    })) {
      const error = new RangeBreach(getRangeBreachMsg(range));
      callOnError(error);
    }
  }, [value, minDate, maxDate]);
  useEffect(() => {
    if (nonWorkingDaysCode && nonWorkingDaysCode.some(day => WEEKDAYS.indexOf(day) < 0)) {
      const error = new InvalidDate(INVALID_NON_WORKING_DAYS);
      callOnError(error);
    }
  }, [nonWorkingDaysCode]);
  useEffect(() => {
    if (weekStartsOnCode && WEEKDAYS.indexOf(weekStartsOnCode) < 0) {
      const error = new InvalidDate(INVALID_WEEK_STARTS_ON);
      callOnError(error);
    }
  }, [weekStartsOnCode, localeCode]);
  const locale = getLocale(localeCode);
  const weekStartsOn = getWeekStartsOn(weekStartsOnCode, locale);
  const nonWorkingDays = getNonWorkingDays(nonWorkingDaysCode);
  /**
   * Handles date selection via calendar
   *
   * @param {Object} param
   * @param {Object} param.date selected date
   * @param {Object} param.event React's synthetic event
   * @returns {void}
   */

  const onDateSelection = ({
    date,
    event
  }) => {
    setTransitionalDate(date);
    callOnDateChange({
      date,
      event
    });
    setTimeout(() => {
      toggle(ref);
    }, CLOSE_DELAY);
  };
  /**
   * Handles date input via date field
   *
   * @param {Object} event React's synthetic event
   *
   * @returns {void}
   */


  const onDateInput = event => {
    const {
      value: fieldValue
    } = event.target;

    if (!fieldValue) {
      callOnDateChange({
        date: null,
        event
      });
      return;
    }

    if (isInputUnchanged(fieldValue, value)) return;
    const parsedDate = parseDate(fieldValue);

    if (!isValidDate(parsedDate)) {
      callOnError(new UserInvalidDate(USER_INVALID_DATE));
      return;
    }

    if (!isWithinRange({
      date: parsedDate,
      minDate: range.min,
      maxDate: range.max
    })) {
      callOnError(new UserRangeBreach(getUserRangeBreachMsg(range)));
      return;
    }

    callOnDateChange({
      date: parsedDate,
      event
    });
  };

  const contextObject = useMemo(() => ({
    size: getSize(sizes, size),
    disabled
  }), [size, disabled]);

  const toggleCalendar = event => {
    event.stopPropagation();
    toggle(ref);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.DatePicker, {
      [styles.stretch]: stretch
    })
  }, /*#__PURE__*/React.createElement(Provider$1, {
    value: {
      plugin: /*#__PURE__*/React.createElement(IconButton, {
        ref: iconRef,
        nude: true,
        size: getSize(sizes, size),
        disabled: disabled,
        icon: /*#__PURE__*/React.createElement(Calendar$1, null),
        type: "button",
        "data-testid": "date-button",
        onClick: toggleCalendar,
        "aria-haspopup": "true",
        "aria-label": calendarLabel || (isValidDate(value) ? `selected date is ${fieldValue}` : `choose date`)
      }),
      renderText: !showNative
    }
  }, /*#__PURE__*/React.createElement(Datefield, _extends({}, rest, {
    placeholder: placeholder || displayFormat(),
    value: fieldValue,
    stretch: stretch,
    disabled: disabled,
    min: formatDate(range.min),
    max: formatDate(range.max),
    invalid: invalid,
    size: getSize(sizes, size),
    onChange: ({
      target: {
        value
      }
    }) => {
      setFieldValue(value);
    },
    onBlur: event => onDateInput(event)
  }))), /*#__PURE__*/React.createElement(Popover, {
    target: /*#__PURE__*/React.createElement("span", {
      className: cn(styles.dummyTarget, {
        [styles.largeDummyTarget]: isLargeSized(size)
      })
    }),
    placement: "auto-end",
    smartFocus: false,
    onClose: () => iconRef.current.focus(),
    ref: ref
  }, /*#__PURE__*/React.createElement(DateProvider, {
    value: contextObject
  }, /*#__PURE__*/React.createElement(Calendar, {
    onDateSelection: onDateSelection,
    onDateTransition: onDateTransition,
    transitionalDate: transitionalDate,
    locale: locale,
    minDate: range.min,
    maxDate: range.max,
    weekStartsOn: weekStartsOn,
    value: value,
    nonWorkingDays: nonWorkingDays,
    variant: variant,
    calendarId: calendarId,
    nextMonthLabel: nextMonthLabel,
    previousMonthLabel: previousMonthLabel
  }))));
};
DatePicker.propTypes = {
  /**
   *
   * Selected date. Pass `null` or `undefined` to set today's date
   *
   * **Note**: _DatePicker will show the console warnings if `value` or `onDateChange` props are missing, as they are needed to update the date.
   * Hence, we recommend you to pass both the props._
   *
   */
  value: PropTypes.object,

  /**
   * Callback called when date is either:
   * 1. selected from the calendar
   * 2. typed in the date field
   *
   * The argument is of the shape:
   * - `date`: [Date](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date) instance of selected date
   * - `event`: React's [synthetic event](https://reactjs.org/docs/events.html)
   *
   * ```js
   * funtion onDateChange({ date, event }) {}
   * ```
   *
   * **Note**: _DatePicker will show the console warnings if `value` or `onDateChange` props are missing, as they are needed to update the date.
   * Hence, we recommend you to pass both the props._
   */
  onDateChange: PropTypes.func,

  /**
   * Minimum selectable date (inclusive)
   */
  minDate: PropTypes.object,

  /**
   * Maximum selectable date (inclusive)
   */
  maxDate: PropTypes.object,

  /**
   * The language locale to format days and months. It also determines the first day of the week.
   * DatePicker provides a set of pre-defined locale codes:
   * - `"en-US"` - English (United States)
   * - `"en-IN"` - English (India)
   * - `"id"` - Bahasa (Indonesia)
   * - `"vi"` - Vietnamese
   * - `"th"` - Thai
   *
   * You can pass other locales as an object. Checkout [date-fns](https://github.com/date-fns/date-fns/blob/master/src/locale/index.js) for the available locales.
   */
  locale: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf(["en-US", "en-IN", "id", "vi", "th"])]),

  /**
   * Override first day of the week of a locale.
   * As the default locale is ‘en-US', the week starts on Sunday by default
   *
   * The value should be one of `"sun"`, `"mon"`, `"tue"`, `"wed"`, `"thu"`, `"fri"`, `"sat"`
   */
  weekStartsOn: PropTypes.oneOf(["sun", "mon", "tue", "wed", "thu", "fri", "sat"]),

  /**
   * The non working days (weekends) for a week.
   *
   * The value can be any combination of `"sun"`, `"mon"`, `"tue"`, `"wed"`, `"thu"`, `"fri"`, `"sat"`
   */
  nonWorkingDays: PropTypes.arrayOf(PropTypes.oneOf(["sun", "mon", "tue", "wed", "thu", "fri", "sat"])),

  /**
   * Renders DatePicker in error state
   */
  invalid: PropTypes.bool,

  /**
   * Callback called when an error occurs. The argument is an instance of JS [Error](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error)
   *
   * ```js
   * funtion onError(error) {}
   * ```
   */
  onError: PropTypes.func,

  /**
   * Renders pro variant.
   */
  pro: PropTypes.bool,

  /**
   * Renders dob variant
   */
  dob: PropTypes.bool,

  /**
   * Renders event variant
   */
  event: PropTypes.bool,

  /**
   * Size of the DatePicker. Available values are `"m"` and `"l"`
   */
  size: PropTypes.oneOf(["m", "l"]),

  /**
   * Placeholder for date field
   */
  placeholder: PropTypes.string,

  /**
   * Enables date field to take parent's width.
   *
   * _The size of the calendar does not change_
   */
  stretch: PropTypes.bool,

  /**
   * Disables the DatePicker
   */
  disabled: PropTypes.bool,

  /**
   * ID to uniquely identify the calendar.
   *
   * _For accessibility_
   */
  calendarId: PropTypes.string,

  /**
   * Override the `aria-label` of the calendar toggle button. The label should have values for 2 cases:
   * 1. If no date selected - the label should ask the user to select a date
   * 2. If date is selected - the label should also mention the selected date along with case 1
   *
   * _For accessibility_
   */
  calendarLabel: PropTypes.string,

  /**
   * Override `aria-label` for the button to navigate to next month
   *
   * _For accessibility_
   */
  nextMonthLabel: PropTypes.string,

  /**
   * Override `aria-label` for the button to navigate to previous month
   *
   * _For accessibility_
   */
  previousMonthLabel: PropTypes.string
};
DatePicker.defaultProps = {
  locale: "en-US",
  nonWorkingDays: ["sat", "sun"],
  size: "m",
  stretch: false,
  disabled: false,
  placeholder: "yyyy-mm-dd"
};

export { InvalidDate, InvalidRange, RangeBreach, UserInvalidDate, UserRangeBreach, DatePicker as default };
